import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Icon } from "semantic-ui-react";

import { CustomerCard, DATE_FORMATS_FNS, ReactTable } from "components";
import { useSnoozeDepartmentOptions, useSnoozeTypeOptions } from "components/SnoozeModal/hooks";
import { getSnoozeStatusOptions } from "components/SnoozeModal/util";
import { SnoozeActions } from "modules/AppointmentDetails/components/SnoozedItems/components";
import { useAppointmentDealer } from "modules/AppointmentDetails/hooks";
import { formatDate } from "utils/dateHelperFunctions";

import type { OnChangeFn, PaginationState } from "@tanstack/react-table";
import type { Appointment, Snooze } from "models";
import type { Dispatch, SetStateAction } from "react";
import type { ItemWithHistory } from "utils/itemsWithHistory";
import type { PaginatedItems } from "utils/paginatedItems";

type SnoozeTableProps = {
  appointment: Appointment;
  paginatedSnoozedItemsHistory: PaginatedItems<ItemWithHistory<Snooze>> | undefined;
  loading: boolean;
  setPage: Dispatch<SetStateAction<number>>;
};

const PAGE_SIZE = 10;
const columnHelper = createColumnHelper<ItemWithHistory<Snooze>>();

export const SnoozedItemsHistoryTable = ({ appointment, paginatedSnoozedItemsHistory, loading, setPage }: SnoozeTableProps) => {
  const { t } = useTranslation();

  const totalPages = paginatedSnoozedItemsHistory?.nb_pages ?? 1;
  const totalItems = paginatedSnoozedItemsHistory?.nb_items ?? 0;

  const [isExpanded, setIsExpanded] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: totalPages });

  const { snoozeStatusOptions } = getSnoozeStatusOptions(t);
  const appointmentDealer = useAppointmentDealer(appointment);
  const { snoozeTypeOptions } = useSnoozeTypeOptions(appointmentDealer);
  const { snoozeDepartmentOptions } = useSnoozeDepartmentOptions();

  const handlePaginationChange: OnChangeFn<PaginationState> = updater => {
    setPagination(currentPagination => {
      const pagination = typeof updater === "function" ? updater(currentPagination) : updater;
      setPage(pagination.pageIndex + 1);
      return pagination;
    });
  };

  if (!totalItems) return null;

  const columns = [
    columnHelper.accessor(row => row, {
      id: "title",
      header: t("v8_title", "Title"),
      cell: ({ row: { original } }) => <span>{original.question_result?.title || original.intervention?.title || ""}</span>,
      size: 175
    }),
    columnHelper.accessor(row => row, {
      id: "snooze_type_id",
      header: t("v8_snooze_type", "Snooze type"),
      cell: ({ row: { original } }) => <span>{snoozeTypeOptions.find(type => type.value === original.snooze_type_id)?.text || ""}</span>
    }),
    columnHelper.accessor(row => row, {
      id: "snooze_date",
      header: t("v8_snooze_date", "Snooze date"),
      cell: ({ row: { original } }) => <span>{original.snooze_date ? formatDate(original.snooze_date, DATE_FORMATS_FNS.dateMonthYear) : ""}</span>
    }),
    columnHelper.accessor(row => row, {
      id: "snooze_status_id",
      header: t("v8_snooze_status", "Snooze status"),
      cell: ({ row: { original } }) => <span>{snoozeStatusOptions.find(status => status.value === original.snooze_status_id)?.text || ""}</span>
    }),
    columnHelper.accessor(row => row, {
      id: "snooze_department_id",
      header: t("v8_department", "Department"),
      cell: ({ row: { original } }) => <span>{snoozeDepartmentOptions.find(status => status.value === original.snooze_department_id)?.text || ""}</span>
    }),
    columnHelper.accessor(row => row, {
      id: "note",
      header: t("v8_note", "Note"),
      cell: ({ row: { original } }) => <span>{original.note}</span>,
      size: 200
    }),
    columnHelper.accessor(row => row, {
      id: "wo",
      header: "WO #",
      cell: ({ row: { original } }) => {
        if (!original.appointment) return null;

        return (
          <span>
            <Link to={`/appointments/${original.appointment.id}`} target="_blank">
              <Icon className="arrow up right from square" />
              {original.appointment.wo_nr || ""}
            </Link>
          </span>
        );
      },
      size: 100
    }),
    columnHelper.accessor(row => row, {
      id: "action",
      header: "",
      cell: ({ row: { original } }) => {
        if (!original.appointment) return null;

        const interventionHistoryItem = original.history[0].intervention;
        const questionResultHistoryItem = original.history[0].question_result;
        const intervention = interventionHistoryItem ? { ...interventionHistoryItem, snooze_history: original.history } : undefined;
        const questionResult = questionResultHistoryItem ? { ...questionResultHistoryItem, snooze_history: original.history } : undefined;

        return (
          <div className="action-container">
            <CustomerCard
              contractor={original.appointment.customer_contract}
              driver={original.appointment.customer_driver}
              owner={original.appointment.customer_owner}
              driverOptedOutFromImporting={original.appointment.driver_opted_out_from_importing}
              ownerOptedOutFromImporting={original.appointment.owner_opted_out_from_importing}
              contractorOptedOutFromImporting={original.appointment.contractor_opted_out_from_importing}
            />
            <SnoozeActions intervention={intervention} questionResult={questionResult} snoozeItem={original} appointment={appointment} />
          </div>
        );
      },
      size: 100
    })
  ];

  return (
    <div className={`SnoozeTable ${totalPages > 1 ? "withPagination" : ""}`}>
      <div className={`SnoozeTable-header ${isExpanded ? "expanded" : ""}`}>
        <h3>{`${t("v8_snoozed_items_history", "Snoozed Items History")} ${totalItems > 0 ? `(${totalItems})` : ""}`}</h3>
        <Icon className={`chevron ${isExpanded ? "down" : "right"} pointer`} onClick={() => setIsExpanded(prev => !prev)} />
      </div>
      {isExpanded && (
        <ReactTable
          isFetching={loading}
          columns={columns}
          data={paginatedSnoozedItemsHistory?.items}
          pageCount={totalPages}
          pagination={totalPages > 1 ? pagination : undefined}
          onPaginationChange={handlePaginationChange}
          totalItems={totalItems}
          pageSize={PAGE_SIZE}
        />
      )}
    </div>
  );
};
